import { Skeleton } from '@/components/ui/skeleton';
import { useInventoryDrones } from '@/lib/api/queries';
import type { InventoryDrone } from '@/lib/api/types';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import { djiOnline } from '../inv/djiStatus';
import { useNow } from '../inv/hooks/useNow';
import { useFilteredFlights, useFlightsStore } from './FlightsStore';

function droneOnline(d: InventoryDrone): boolean {
  if (d.dji_status) return djiOnline(d);
  return d.custodian_status === 'online' || d.gcs_plugin_status === 'online';
}

export function AircraftPanel() {
  const { data: drones, isPending, isError, refetch } = useInventoryDrones();
  const { data: flights } = useFilteredFlights();
  const now = useNow(
    5_000,
    (drones ?? []).some((d) => d.dji_status != null),
  );

  const idMode = useFlightsStore((s) => s.idMode);
  const showOnline = useFlightsStore((s) => s.showOnline);
  const showOffline = useFlightsStore((s) => s.showOffline);
  const selected = useFlightsStore((s) => s.selectedSerial);
  const selectSerial = useFlightsStore((s) => s.selectSerial);
  const setIdMode = useFlightsStore((s) => s.setIdMode);
  const toggleState = useFlightsStore((s) => s.toggleState);

  // One pass over the flight list instead of a filter per aircraft card.
  const countBySerial = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of flights ?? []) m.set(f.aircraft_serial, (m.get(f.aircraft_serial) ?? 0) + 1);
    return m;
  }, [flights]);

  // The roster is the FULL inventory, including aircraft with zero flights —
  // "this aircraft has never flown" is a real answer the panel should give.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `now` re-runs the DJI telemetry-age check
  const visible = useMemo(
    () => (drones ?? []).filter((d) => (droneOnline(d) ? showOnline : showOffline)),
    [drones, showOnline, showOffline, now],
  );

  useEffect(() => {
    if (!selected && visible.length > 0) selectSerial(visible[0]!.serial_number);
  }, [visible, selected, selectSerial]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0">
        <span className="text-[0.923em] font-bold">Aircraft</span>
      </header>

      <div className="px-3 py-2 border-b border-(--color-border) shrink-0 space-y-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-[0.692em] text-(--color-text3) w-10 shrink-0">State</span>
          <Chip on={showOnline} tone="ok" onClick={() => toggleState('online')}>
            Online
          </Chip>
          <Chip on={showOffline} tone="crit" onClick={() => toggleState('offline')}>
            Offline
          </Chip>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[0.692em] text-(--color-text3) w-10 shrink-0">Show</span>
          <Chip on={idMode === 'callsign'} onClick={() => setIdMode('callsign')}>
            Callsign
          </Chip>
          <Chip on={idMode === 'serial'} onClick={() => setIdMode('serial')}>
            Serial
          </Chip>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {isPending &&
          ['s0', 's1', 's2', 's3'].map((k) => <Skeleton key={k} className="h-[72px] w-full" />)}

        {isError && (
          <div className="text-[0.769em] text-(--color-red) text-center p-4 space-y-2">
            <div>Failed to load aircraft.</div>
            <button
              type="button"
              onClick={() => refetch()}
              className="text-(--color-green) underline"
            >
              Retry
            </button>
          </div>
        )}

        {!isPending && !isError && visible.length === 0 && (
          <div className="p-4 text-center text-[0.846em] text-(--color-text3)">
            No aircraft match the current filters.
          </div>
        )}

        {visible.map((d) => {
          const primary = idMode === 'serial' ? d.serial_number : (d.ec_callsign ?? d.display_name);
          const secondary =
            idMode === 'serial' ? (d.ec_callsign ?? d.display_name) : d.serial_number;
          const count = countBySerial.get(d.serial_number) ?? 0;
          const online = droneOnline(d);
          return (
            <button
              key={d.drone_id}
              type="button"
              onClick={() => selectSerial(d.serial_number)}
              className={cn(
                'w-full text-left rounded-md border p-2.5 transition-colors',
                d.serial_number === selected
                  ? 'border-(--color-green) bg-(--color-green)/5'
                  : 'border-(--color-border) hover:bg-(--color-bg)',
              )}
            >
              <div className="flex items-center gap-2">
                <span className="text-[0.846em] font-bold truncate">{primary}</span>
                <span
                  className={cn(
                    'ml-auto text-[0.615em] font-bold px-1.5 py-0.5 rounded-full shrink-0',
                    online
                      ? 'bg-(--color-green-bg) text-(--color-green)'
                      : 'bg-(--color-red-bg) text-(--color-red)',
                  )}
                >
                  {online ? 'Online' : 'Offline'}
                </span>
              </div>
              <div className="text-[0.692em] text-(--color-text2) truncate">
                {d.brand} {d.model}
              </div>
              <div className="flex gap-2 text-[0.692em] text-(--color-text3) font-mono">
                <span className="truncate">{secondary}</span>
                <span className="ml-auto shrink-0">
                  {count} flight{count === 1 ? '' : 's'}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Chip({
  on,
  tone,
  onClick,
  children,
}: {
  on: boolean;
  tone?: 'ok' | 'crit';
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-[0.692em] px-2 py-0.5 rounded-full border transition-colors',
        !on && 'border-(--color-border) text-(--color-text3)',
        on && tone === 'ok' && 'border-(--color-green) bg-(--color-green-bg) text-(--color-green)',
        on && tone === 'crit' && 'border-(--color-red) bg-(--color-red-bg) text-(--color-red)',
        on && !tone && 'border-(--color-green) bg-(--color-green-bg) text-(--color-green)',
      )}
    >
      {children}
    </button>
  );
}
